import React, { useState } from "react"
import {
  Box, Flex, Input, Text,
} from "@chakra-ui/react"
import { format } from "date-fns"
import { Combobox } from "components/Combobox"
import Icon from "components/elements/Icon"
import { Filter } from "sharedTypes"
import { FilterFieldConfig, FilterOptionsContext, FilterValueOption } from "./types"
import { ChipSize, CHIP_SIZES } from "./FilterChip"
import MultiValuePicker from "./MultiValuePicker"
import NumberPicker from "./NumberPicker"
import DatePicker, { utcIsoToLocalDay } from "./DatePicker"

// Stacked-icons display for the chip's value cell when 2+ values are
// selected — up to three icons overlap with negative margin, followed by
// the count. Mirrors the Ariane / Linear pattern.
const renderValueDisplay = (
  filter: Filter,
  options: FilterValueOption[],
  deletedLabel: string | undefined,
  size: ChipSize,
) => {
  const tokens = CHIP_SIZES[size]

  if (filter.values.length === 0) return "Select…"
  if (filter.values.length === 1) {
    const opt = options.find((o) => o.value === filter.values[0])
    // Orphan: stored value whose option is gone (deleted tag, etc.).
    // Show the field's deletedLabel in italic gray with the raw id as
    // a tooltip — better than the bare id and the user knows what to
    // clean up.
    const isOrphan = !opt && Boolean(deletedLabel)
    const display = opt?.label ?? (isOrphan ? deletedLabel : filter.values[0])

    return (
      <Flex align="center" gap={tokens.valueDisplayGap} minW={0}>
        {opt?.renderIcon
          ? opt.renderIcon({ boxSize: tokens.valueIconSize })
          : opt?.iconColor && (
            <Icon icon={opt.icon ?? "dot"} size={tokens.valueIconSize} color={opt.iconColor} />
          )}
        <Text
          as="span"
          maxW={tokens.valueMaxW}
          minW={0}
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          fontStyle={isOrphan ? "italic" : undefined}
          fontWeight={isOrphan ? "normal" : undefined}
          color={isOrphan ? "gray.500" : undefined}
          title={isOrphan ? filter.values[0] : undefined}
          // 2px of room on the right so italic descenders/ascenders
          // don't get nibbled by the overflow: hidden above.
          pr={isOrphan ? "2px" : undefined}
        >
          {display}
        </Text>
      </Flex>
    )
  }

  // Only stack icons for options that actually have one — fields like
  // Seating / Email Validity have no per-value icon, and faking gray dots
  // there reads as "what does that even represent?". If none of the selected
  // options have icons, just show the count.
  const iconedOpts = options
    .filter((o) => filter.values.includes(o.value) && (o.renderIcon || o.iconColor))

  return (
    <Flex align="center" gap={tokens.valueDisplayGap}>
      {iconedOpts.length > 0 && (
        <Flex align="center">
          {iconedOpts.slice(0, 3).map((opt, i) => (
            // bg-white disc behind each icon so the icon above occludes
            // the one underneath in the overlap zone — without it, icons
            // with transparent regions (tag, heart, …) show the lower
            // icon bleeding through.
            <Box
              key={opt.value}
              ml={i === 0 ? 0 : -1.5}
              bg="white"
              borderRadius="full"
              p="1px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {opt.renderIcon
                ? opt.renderIcon({ boxSize: tokens.valueIconSize })
                : <Icon icon={opt.icon ?? "dot"} size={tokens.valueIconSize} color={opt.iconColor!} />}
            </Box>
          ))}
        </Flex>
      )}
      <Text as="span">{filter.values.length} selected</Text>
    </Flex>
  )
}

type Props = {
  filter: Filter
  config: FilterFieldConfig
  context: FilterOptionsContext
  size?: ChipSize
  onChange: (values: string[]) => void
  // Called when the user closes the picker with zero values still selected.
  // We let users transiently empty the chip mid-edit, then tear it down on
  // popover close — rather than yanking the whole chip out from under them
  // the moment they uncheck the last value.
  onRemove: () => void
}

const valueCellProps = (size: ChipSize) => {
  const tokens = CHIP_SIZES[size]

  return {
    px: tokens.px,
    h: "100%",
    minW: 0,
    display: "flex",
    alignItems: "center",
    fontSize: tokens.fontSize,
    fontWeight: "semibold" as const,
    cursor: "pointer",
    color: "gray.800",
    _hover: { bg: "gray.50" },
  } as const
}

// Tabular nums keep numeric chips' width stable when the value changes
// width (1 vs 11). Date chips don't get this — equal-width digits make
// "May 17" look like "May 1 7".
const TABULAR_NUMS = { sx: { fontVariantNumeric: "tabular-nums" } }

const EnumValueEditor = ({
  filter, config, context, size = "md", onChange, onRemove,
}: Props) => {
  const options = config.getOptions?.(context) ?? []
  // Stored values whose option no longer exists in the live set.
  // Synthesised so the chip and picker can surface them as removable.
  const orphans: FilterValueOption[] = config.deletedLabel
    ? filter.values
      .filter((v) => !options.some((o) => o.value === v))
      .map((v) => ({ value: v, label: config.deletedLabel! }))
    : []
  const display = renderValueDisplay(filter, options, config.deletedLabel, size)
  const cellProps = valueCellProps(size)

  if (config.multiSelect) {
    return (
      <Combobox.Root
        items={[]}
        getItemKey={() => ""}
        getItemLabel={() => ""}
        autoHighlightFirstAction={false}
      >
        <Combobox.Trigger>
          <Box {...cellProps} data-testid="filter-chip-value">
            {display}
          </Box>
        </Combobox.Trigger>
        <Combobox.Content w="280px" arrowBg="white" growToContent>
          {/* Same picker the AddFilterPopover step 2 uses. The wrapping
              Combobox.Root only exists for the Popover infrastructure and
              for Combobox.Action's close hook — items=[] disables the
              built-in list. MultiValuePicker drives everything else. */}
          <MultiValuePicker
            label={config.label}
            options={options}
            orphans={orphans}
            initialValues={filter.values}
            onCommit={(values) => {
              if (values.length === 0) onRemove()
              else onChange(values)
            }}
          />
        </Combobox.Content>
      </Combobox.Root>
    )
  }

  // Single-select fallback (no enum field is single-select in v1.5, but the
  // branch is here so the config flag stays meaningful for future fields).
  const selected = options.find((o) => o.value === filter.values[0]) ?? null

  return (
    <Combobox.Root
      items={options}
      getItemKey={(o) => o.value}
      getItemLabel={(o) => o.label}
      value={selected}
      onChange={(o) => onChange(o ? [o.value] : [])}
    >
      <Combobox.Trigger>
        <Box {...cellProps} data-testid="filter-chip-value">
          {display}
        </Box>
      </Combobox.Trigger>
      <Combobox.Content w="280px" arrowBg="white">
        <Combobox.Input
          placeholder={`Search ${config.label}`}
          py={2}
        />
        <Combobox.List maxH="280px">
          {(item: FilterValueOption) => (
            <Combobox.Item item={item}>
              <Flex align="center" gap={2}>
                {item.renderIcon
                  ? item.renderIcon()
                  : item.iconColor && (
                    <Icon icon={item.icon ?? "dot"} size={3} color={item.iconColor} />
                  )}
                <Text>{item.label}</Text>
              </Flex>
            </Combobox.Item>
          )}
        </Combobox.List>
        <Combobox.Empty py={4} textAlign="center" fontSize="md" color="gray.500">
          {(query) => (query ? `No matches for "${query}"` : "No options")}
        </Combobox.Empty>
      </Combobox.Content>
    </Combobox.Root>
  )
}

// Same Combobox.Root wrap as EnumValueEditor — items=[] is a no-op carrier
// that gives the chip-edit popover the same closeOnSelect / arrow / blur
// behaviour as the add flow, with NumberPicker / DatePicker doing the work.
type ScalarEditorProps = Pick<Props, "filter" | "onChange"> & { size: ChipSize }

const NumberValueEditor = ({ filter, size, onChange }: ScalarEditorProps) => {
  const display = filter.values[0] ?? "0"

  return (
    <Combobox.Root
      items={[]}
      getItemKey={() => ""}
      getItemLabel={() => ""}
      autoHighlightFirstAction={false}
    >
      <Combobox.Trigger>
        <Box {...valueCellProps(size)} {...TABULAR_NUMS} data-testid="filter-chip-value">
          {display}
        </Box>
      </Combobox.Trigger>
      <Combobox.Content w="220px" arrowBg="white">
        <NumberPicker initialValue={filter.values[0] ?? "0"} onCommit={onChange} />
      </Combobox.Content>
    </Combobox.Root>
  )
}

const DateValueEditor = ({ filter, size, onChange }: ScalarEditorProps) => {
  const raw = filter.values[0]
  const initial = raw ? utcIsoToLocalDay(raw) : null
  const display = initial ? format(initial, "MMM d, yyyy") : "Pick date…"

  return (
    <Combobox.Root
      items={[]}
      getItemKey={() => ""}
      getItemLabel={() => ""}
      autoHighlightFirstAction={false}
    >
      <Combobox.Trigger>
        <Box {...valueCellProps(size)} data-testid="filter-chip-value">
          {display}
        </Box>
      </Combobox.Trigger>
      <Combobox.Content w="auto" arrowBg="white">
        <DatePicker initialValue={raw} onCommit={onChange} />
      </Combobox.Content>
    </Combobox.Root>
  )
}

const TEXT_EDITOR_ITEMS: never[] = []
const textEditorItemKey = () => ""
const textEditorItemLabel = () => ""

// Text-type editor: inline Input for contains / does not contain operators.
const TextValueEditor = ({ filter, size, onChange }: ScalarEditorProps) => {
  const tokens = CHIP_SIZES[size]
  const current = filter.values[0] ?? ""
  const [draft, setDraft] = useState(current)
  const display = current || <Text as="span" color="gray.400" fontWeight="normal">Type…</Text>

  return (
    <Combobox.Root
      items={TEXT_EDITOR_ITEMS}
      getItemKey={textEditorItemKey}
      getItemLabel={textEditorItemLabel}
      autoHighlightFirstAction={false}
    >
      <Combobox.Trigger>
        <Box {...valueCellProps(size)} data-testid="filter-chip-value">
          {display}
        </Box>
      </Combobox.Trigger>
      <Combobox.Content w="240px" arrowBg="white">
        <Box px={3} py={2}>
          <Input
            autoFocus
            size="sm"
            fontSize={tokens.fontSize}
            value={draft}
            placeholder="Type to filter…"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                (e.target as HTMLElement).blur()
              }
            }}
            onBlur={() => onChange(draft ? [draft] : [])}
          />
        </Box>
      </Combobox.Content>
    </Combobox.Root>
  )
}

const FilterChipValue = ({
  filter, config, context, size = "md", onChange, onRemove,
}: Props) => {
  if (config.type === "enum") {
    return (
      <EnumValueEditor
        filter={filter}
        config={config}
        context={context}
        size={size}
        onChange={onChange}
        onRemove={onRemove}
      />
    )
  }
  if (config.type === "number") {
    return <NumberValueEditor filter={filter} size={size} onChange={onChange} />
  }
  if (config.type === "date") {
    return <DateValueEditor filter={filter} size={size} onChange={onChange} />
  }
  if (config.type === "text") {
    return <TextValueEditor filter={filter} size={size} onChange={onChange} />
  }

  return null
}

export default FilterChipValue
